import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlaceIcon from '@mui/icons-material/Place';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import GroupsIcon from '@mui/icons-material/Groups';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { BrandFavicon } from '../brand/BrandFavicon';
import { EngineLogo, ENGINE_LABEL } from '../../pages/dashboard/EngineLogo';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../contexts/BrandContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import CadenceEditor, { AXIS_LABELS } from '../brand/CadenceEditor';
import { SCOPE_LABELS } from '../brand/strategy-sections';
import {
  describeCadence,
  resolveEntitlement,
  SCHEDULABLE_AUDIT_TYPES,
  type Brand,
  type Cadence,
  type UsageMetric,
  type BrandUsage,
  type SchedulableAuditType,
} from '@traaker/shared';

interface UsageResponse {
  tier: string;
  metrics: UsageMetric[];
  cadenceConfigurable: boolean;
  perBrand: BrandUsage[];
}

export default function BrandsSettings() {
  const { t } = useTranslation();
  const { brands, refreshBrands, setActiveBrand } = useBrand();
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [brandDialog, setBrandDialog] = useState<{ open: boolean; brand?: Brand }>({ open: false });
  const [message, setMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; brand?: Brand }>({ open: false });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const canManage =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';
  const tier = tenant?.tier || 'basic';
  // Authoritative brand limit: tier default + packs + override, ceiling-clamped.
  // resolveEntitlement is the single source of truth (no stale local map).
  const brandLimit = resolveEntitlement('brands', tier, {
    packs: tenant?.settings?.activePacks?.brands,
    override: tenant?.settings?.quotaOverrides?.brands,
  }).limit;
  const canAddBrand = brandLimit === -1 || brands.length < brandLimit;
  const totalLocations = brands.reduce((sum, b) => sum + (b.locations?.length ?? 0), 0);
  // Agency defaults to unlimited brands, EXCEPT Agency XS whose 15-brand cap is
  // carried as quotaOverrides.brands. The agency layout renders BrandUsageCards
  // instead of UsageSummary, so without this chip a capped tenant would hit the
  // wall with no counter anywhere. Keyed on `brandLimit !== -1`, not on the plan.
  const showBrandCap = tier === 'agency' && brandLimit !== -1;

  // Add-brand click: route to onboarding when within quota, otherwise show the
  // upgrade popup (which routes to the plans page).
  const handleAddBrandClick = () => {
    if (canAddBrand) navigate('/onboarding/brand/new');
    else setUpgradeOpen(true);
  };

  // Tenant-wide quota usage summary — ONE call on mount (4 DDB reads server-side,
  // no per-brand fan-out). Refreshed when the brand list changes (add/delete).
  useEffect(() => {
    let cancelled = false;
    api
      .get<UsageResponse>('/metering/usage')
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
  }, [brands.length]);

  const handleToggleExpand = (brandId: string) => {
    setExpanded(expanded === brandId ? null : brandId);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialog({ open: false });
    setDeleteConfirmation('');
  };

  const handleDeleteBrand = async () => {
    if (!deleteDialog.brand) return;
    const target = deleteDialog.brand;
    if (deleteConfirmation.trim().toLowerCase() !== target.name.trim().toLowerCase()) return;
    setDeleting(true);
    try {
      await api.delete(`/brands/${target.brandId}`);
      await refreshBrands();
      setDeleteDialog({ open: false });
      setDeleteConfirmation('');
      setMessage(t('brandsSettings.toast.brandDeleted', { name: target.name }));
    } catch {
      setMessage(t('brandsSettings.toast.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const goManageLocations = (brand: Brand) => {
    setActiveBrand(brand);
    navigate('/strategy');
  };

  return (
    <Box>
      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} data-tour="brands-manage">
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {t('brandsSettings.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {/* Plain counts — quota display + management live in Brand Knowledge
                  (zones + languages) and the plan/billing page, not here. */}
              {t('brandsSettings.brandCount', { count: brands.length })}
              {' · '}
              {t('brandsSettings.zoneCount', { count: totalLocations })}
            </Typography>
            {showBrandCap && (
              <Chip
                size="small"
                variant="outlined"
                color={canAddBrand ? 'default' : 'warning'}
                label={t('brandsSettings.brandCap', { used: brands.length, limit: brandLimit })}
              />
            )}
          </Box>
        </Box>
        {canManage && (
          <Tooltip title={canAddBrand ? '' : t('brandsSettings.brandCapReached', { limit: brandLimit })}>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={handleAddBrandClick}
            >
              {t('brandsSettings.addBrand')}
            </Button>
          </Tooltip>
        )}
      </Box>

      {usage && usage.tier === 'agency' ? (
        <BrandUsageCards
          usage={usage}
          brands={brands}
          canManage={canManage}
          onEdit={(brand) => setBrandDialog({ open: true, brand })}
          onDelete={(brand) => {
            setDeleteConfirmation('');
            setDeleteDialog({ open: true, brand });
          }}
        />
      ) : (
        <>
          {usage && <UsageSummary usage={usage} />}
          {brands.map((brand) => (
        <Card key={brand.brandId} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleToggleExpand(brand.brandId)}
              >
                {expanded === brand.brandId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>
                  {brand.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {brand.url || t('brandsSettings.noWebsite')}
                </Typography>
              </Box>
              <Chip
                icon={<PlaceIcon sx={{ fontSize: '14px !important' }} />}
                label={t('brandsSettings.zoneCount', { count: brand.locations?.length ?? 0 })}
                size="small"
                variant="outlined"
              />
              {canManage && (
                <>
                  <IconButton size="small" onClick={() => setBrandDialog({ open: true, brand })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeleteConfirmation('');
                      setDeleteDialog({ open: true, brand });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            <Collapse in={expanded === brand.brandId}>
              <Box sx={{ mt: 1.5, pl: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                    {t('brandsSettings.geoZones')}
                  </Typography>
                  {canManage && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => goManageLocations(brand)}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('brandsSettings.manage')} →
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                  {(brand.locations ?? []).map((loc) => (
                    <Chip
                      key={loc.locationId}
                      label={`${loc.label}${loc.scope ? ` (${SCOPE_LABELS[loc.scope]})` : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {(brand.locations?.length ?? 0) === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {canManage ? t('brandsSettings.noZoneAdmin') : t('brandsSettings.noZoneMember')}
                    </Typography>
                  )}
                </Box>

                {(brand.competitors?.length ?? 0) > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      {t('brandsSettings.competitors')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      {(brand.competitors ?? []).map((c) => (
                        <Chip key={c} label={c} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {usage && usage.tier !== 'agency' && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                        {t('brandsSettings.auditCadence')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {usage.cadenceConfigurable ? t('brandsSettings.cadenceCustomizable') : t('brandsSettings.cadenceIncluded', { tier: usage.tier })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      <CadenceStrip
                        axes={(usage.perBrand ?? []).find((b) => b.brandId === brand.brandId)?.cadence}
                      />
                    </Box>
                  </Box>
                )}

              </Box>
            </Collapse>
          </CardContent>
        </Card>
          ))}
        </>
      )}

      {brands.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('brandsSettings.noBrandConfigured')}
            </Typography>
          </CardContent>
        </Card>
      )}

      <BrandDialog
        {...brandDialog}
        tier={tier}
        onClose={() => setBrandDialog({ open: false })}
        onSaved={refreshBrands}
        onCreated={(brandId) => navigate(`/onboarding/brand/${brandId}`)}
      />
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('brandsSettings.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('brandsSettings.delete.warning', { name: deleteDialog.brand?.name })}
            <br />
            <br />
            {t('brandsSettings.delete.confirmPrompt', { name: deleteDialog.brand?.name })}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('brandsSettings.delete.nameLabel')}
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {t('brandsSettings.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteBrand}
            disabled={
              deleting ||
              !deleteDialog.brand ||
              deleteConfirmation.trim().toLowerCase() !==
                (deleteDialog.brand?.name || '').trim().toLowerCase()
            }
          >
            {deleting ? t('brandsSettings.delete.deleting') : t('brandsSettings.delete.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <UpgradeBrandDialog
        open={upgradeOpen}
        limit={brandLimit}
        onClose={() => setUpgradeOpen(false)}
        onUpgrade={() => {
          setUpgradeOpen(false);
          navigate('/settings?tab=billing');
        }}
      />
    </Box>
  );
}

// ── Brand-quota reached: upgrade prompt ──

function UpgradeBrandDialog({
  open,
  limit,
  onClose,
  onUpgrade,
}: {
  open: boolean;
  limit: number;
  onClose: () => void;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box
          sx={{
            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
          }}
        >
          <WorkspacePremiumIcon sx={{ fontSize: 30, color: 'primary.main' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('brandsSettings.upgrade.title')}
        </Typography>
        <DialogContentText sx={{ fontSize: 14 }}>
          {t('brandsSettings.upgrade.body', { count: limit })}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose}>{t('brandsSettings.close')}</Button>
        <Button variant="contained" startIcon={<WorkspacePremiumIcon />} onClick={onUpgrade}>
          {t('brandsSettings.upgrade.viewPlans')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Usage summary (tenant-wide gauges) ──

// Display order — most commercially relevant first.
const USAGE_ORDER = ['prompts', 'brands', 'zones', 'languages', 'competitors', 'users', 'tasks'];

function UsageSummary({ usage }: { usage: UsageResponse }) {
  const { t } = useTranslation();
  const byMetric = new Map((usage.metrics ?? []).map((m) => [m.metric, m]));
  const ordered = USAGE_ORDER.map((k) => byMetric.get(k)).filter(Boolean) as UsageMetric[];

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
          {t('brandsSettings.usageShared', { tier: usage.tier })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          {ordered.map((m) => {
            const unlimited = m.limit === -1;
            const over = !unlimited && m.used >= m.limit;
            const near = !unlimited && !over && m.used >= m.limit * 0.8;
            return (
              <Chip
                key={m.metric}
                size="small"
                variant="outlined"
                color={over ? 'error' : near ? 'warning' : 'default'}
                label={`${m.label.fr} · ${m.used}${unlimited ? '' : `/${m.limit}`}`}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Per-brand cadence chip strip (read-only) ──

function CadenceStrip({ axes }: { axes?: Record<string, Cadence> }) {
  const { t } = useTranslation();
  if (!axes) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return (
    <>
      {SCHEDULABLE_AUDIT_TYPES.map((axis) => {
        const c = axes[axis];
        if (!c) return null;
        const off = c === 'off';
        return (
          <Chip
            key={axis}
            size="small"
            variant="outlined"
            label={`${AXIS_LABELS[axis as SchedulableAuditType]} : ${off ? t('brandsSettings.disabled') : describeCadence(c)}`}
            sx={{ opacity: off ? 0.55 : 1 }}
          />
        );
      })}
    </>
  );
}

// ── Agency per-brand feature usage cards ──

// Default AI engines when a brand has no explicit `enginesEnabled` allowlist
// (mirrors the run-path default = primary engines, secondary opt-in only).
const DEFAULT_ENGINE_IDS = ['perplexity', 'openai', 'gemini', 'claude'];

// Subtle strip of activated-model logos for an agency brand card. Each logo
// shows its model name on hover. Reads the per-brand `enginesEnabled` allowlist,
// falling back to the primary roster when unset.
function ActivatedEngines({ brand }: { brand?: Brand }) {
  const saved = brand?.auditConfig?.enginesEnabled;
  const engines = saved && saved.length > 0 ? saved : DEFAULT_ENGINE_IDS;
  if (engines.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {engines.map((e) => (
        <Tooltip key={e} title={ENGINE_LABEL[e] || e} arrow>
          <Box sx={{ display: 'flex', opacity: 0.85, '&:hover': { opacity: 1 } }}>
            <EngineLogo engine={e} size={16} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

// Feature accent palette — mirrors the axis colors used on the Dashboard.
const FEATURE_META: { key: keyof BrandUsage; labelKey: string; color: string; Icon: typeof PublicIcon }[] = [
  { key: 'prompts', labelKey: 'brandsSettings.feature.prompts', color: '#8B5CF6', Icon: ChatBubbleOutlineIcon },
  { key: 'zones', labelKey: 'brandsSettings.feature.zones', color: '#00DDB3', Icon: PublicIcon },
  { key: 'languages', labelKey: 'brandsSettings.feature.languages', color: '#3B82F6', Icon: TranslateIcon },
  { key: 'competitors', labelKey: 'brandsSettings.feature.competitors', color: '#FF6B35', Icon: GroupsIcon },
  { key: 'tasks', labelKey: 'brandsSettings.feature.tasks', color: '#F59E0B', Icon: ChecklistIcon },
];

function BrandUsageCards({
  usage,
  brands,
  canManage,
  onEdit,
  onDelete,
}: {
  usage: UsageResponse;
  brands: Brand[];
  canManage: boolean;
  onEdit: (brand: Brand) => void;
  onDelete: (brand: Brand) => void;
}) {
  const { t } = useTranslation();
  const brandById = new Map(brands.map((b) => [b.brandId, b]));
  const perBrand = usage.perBrand ?? [];

  if (perBrand.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 2,
      }}
    >
      {perBrand.map((bu) => {
        const activeAxes = SCHEDULABLE_AUDIT_TYPES.filter(
          (axis) => bu.cadence?.[axis] && bu.cadence[axis] !== 'off',
        );
        const brand = brandById.get(bu.brandId);
        return (
          <Card
            key={bu.brandId}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'box-shadow .15s, border-color .15s',
              '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
            }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {/* Header band */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <BrandFavicon brandId={bu.brandId} size={40} highRes sx={{ borderRadius: 1.5 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {bu.brandName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {bu.url?.replace(/^https?:\/\/(www\.)?/, '') || t('brandsSettings.noWebsite')}
                  </Typography>
                </Box>
                <ActivatedEngines brand={brand} />
                {canManage && brand && (
                  <>
                    <IconButton size="small" onClick={() => onEdit(brand)} aria-label={t('brandsSettings.edit')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(brand)} aria-label={t('brandsSettings.deleteAria')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>

              <Box sx={{ p: 2 }}>
                {/* Feature usage tiles */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.75 }}>
                  {FEATURE_META.map(({ key, labelKey, color, Icon }) => (
                    <Box
                      key={key}
                      sx={{
                        textAlign: 'center',
                        py: 1,
                        px: 0.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(color, 0.08),
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color, display: 'block', mx: 'auto', mb: 0.25 }} />
                      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {bu[key] as number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        {t(labelKey)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Active audit cadence */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                  <ScheduleIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                  {activeAxes.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('brandsSettings.noScheduledAudit')}
                    </Typography>
                  ) : (
                    activeAxes.map((axis) => (
                      <Chip
                        key={axis}
                        size="small"
                        variant="outlined"
                        label={`${AXIS_LABELS[axis as SchedulableAuditType]} · ${describeCadence(bu.cadence[axis])}`}
                        sx={{ height: 22, fontSize: 11 }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

function BrandDialog({
  open,
  brand,
  tier,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean;
  brand?: Brand;
  tier: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onCreated?: (brandId: string, brandName: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [includeUrls, setIncludeUrls] = useState('');
  const [excludeUrls, setExcludeUrls] = useState('');
  const [emWatchInclude, setEmWatchInclude] = useState('');
  const [emWatchExclude, setEmWatchExclude] = useState('');
  const [showAuditConfig, setShowAuditConfig] = useState(false);
  const [showEarnedMediaWatch, setShowEarnedMediaWatch] = useState(false);
  const [showCadence, setShowCadence] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(brand?.name || '');
      setUrl(brand?.url || '');
      setIncludeUrls((brand?.auditConfig?.includeUrls || []).join('\n'));
      setExcludeUrls((brand?.auditConfig?.excludeUrls || []).join('\n'));
      setEmWatchInclude(
        (brand?.auditConfig?.earnedMediaWatch?.include || []).join('\n'),
      );
      setEmWatchExclude(
        (brand?.auditConfig?.earnedMediaWatch?.exclude || []).join('\n'),
      );
      setShowAuditConfig(
        (brand?.auditConfig?.includeUrls?.length || 0) > 0 ||
        (brand?.auditConfig?.excludeUrls?.length || 0) > 0,
      );
      setShowEarnedMediaWatch(
        (brand?.auditConfig?.earnedMediaWatch?.include?.length || 0) > 0 ||
        (brand?.auditConfig?.earnedMediaWatch?.exclude?.length || 0) > 0,
      );
      setError(null);
    }
  }, [open, brand]);

  const handleSubmit = async () => {
    setError(null);
    const parseLines = (s: string) =>
      s.split('\n').map((l) => l.trim()).filter(Boolean);
    const includes = parseLines(includeUrls);
    const excludes = parseLines(excludeUrls);
    const watchInclude = parseLines(emWatchInclude);
    const watchExclude = parseLines(emWatchExclude);
    try {
      if (brand) {
        // Name + URL are read-only after creation (identity-defining) — only the
        // audit-config fields are editable here. They go through
        // PATCH /brands/:id/audit-config so they merge with cadenceOverride +
        // schedules instead of replacing the full auditConfig.
        await api.patch(`/brands/${brand.brandId}/audit-config`, {
          includeUrls: includes,
          excludeUrls: excludes,
          earnedMediaWatch: {
            include: watchInclude,
            exclude: watchExclude,
          },
        });
        await onSaved();
        onClose();
      } else {
        // Competitors are managed on the Concurrents page — NOT sent here.
        const created = await api.post<Brand>('/brands', { name, url: url || undefined });
        await onSaved();
        onClose();
        if (url && onCreated) {
          onCreated(created.brandId, created.name);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{brand ? t('brandsSettings.dialog.editTitle') : t('brandsSettings.dialog.addTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {brand ? (
          <>
            {/* Name + URL are identity-defining (favicon, DomainProfile, all audit
                history is keyed to the domain) — read-only after creation. To
                change them, delete the brand and create a new one. */}
            <Box>
              <Typography variant="caption" color="text.secondary">{t('brandsSettings.dialog.nameLabel')}</Typography>
              <Typography variant="body1" fontWeight={600}>{brand.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{t('brandsSettings.dialog.websiteLabel')}</Typography>
              <Typography variant="body1">{brand.url || t('brandsSettings.noWebsite')}</Typography>
            </Box>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {t('brandsSettings.dialog.readOnlyNote')}
            </Alert>
          </>
        ) : (
          <>
            <TextField label={t('brandsSettings.dialog.nameLabel')} value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label={t('brandsSettings.dialog.websiteLabel')} value={url} onChange={(e) => setUrl(e.target.value)} fullWidth placeholder="https://example.com" required />
          </>
        )}

        {brand && (
          <>
            {/* Each collapsible section is wrapped in a single container so the
                header + Collapse form ONE flex child of the gap column — a bare
                Collapse as a direct flex-gap child overlaps its neighbour. */}
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowAuditConfig(!showAuditConfig)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showAuditConfig ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.dialog.advancedPageSelection')}
                </Typography>
              </Box>
              <Collapse in={showAuditConfig}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('brandsSettings.dialog.advancedPageHelp')}
                  </Typography>
                  <TextField
                    label={t('brandsSettings.dialog.alwaysInclude')}
                    value={includeUrls}
                    onChange={(e) => setIncludeUrls(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="https://example.com/page-importante&#10;**/category/featured/*"
                    helperText={t('brandsSettings.dialog.alwaysIncludeHelp')}
                  />
                  <TextField
                    label={t('brandsSettings.dialog.alwaysExclude')}
                    value={excludeUrls}
                    onChange={(e) => setExcludeUrls(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="**/drafts/*&#10;**/internal-*"
                    helperText={t('brandsSettings.dialog.alwaysExcludeHelp')}
                  />
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowEarnedMediaWatch(!showEarnedMediaWatch)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showEarnedMediaWatch ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.dialog.earnedMediaWatch')}
                </Typography>
              </Box>
              <Collapse in={showEarnedMediaWatch}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('brandsSettings.dialog.earnedMediaWatchHelp')}
                  </Typography>
                  <TextField
                    label={t('brandsSettings.dialog.watchInclude')}
                    value={emWatchInclude}
                    onChange={(e) => setEmWatchInclude(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="silicon.fr&#10;latribune.fr&#10;lesnumeriques.com"
                    helperText={t('brandsSettings.dialog.watchIncludeHelp')}
                  />
                  <TextField
                    label={t('brandsSettings.dialog.watchExclude')}
                    value={emWatchExclude}
                    onChange={(e) => setEmWatchExclude(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="acme-toolworks.com&#10;acme.health"
                    helperText={t('brandsSettings.dialog.watchExcludeHelp')}
                  />
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box
                onClick={() => setShowCadence(!showCadence)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                {showCadence ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('brandsSettings.auditCadence')}
                </Typography>
              </Box>
              <Collapse in={showCadence}>
                <Box sx={{ pt: 2 }}>
                  <CadenceEditor
                    brandId={brand.brandId}
                    tier={tier}
                    cadenceOverride={brand.auditConfig?.cadenceOverride}
                    onSaved={onSaved}
                  />
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('brandsSettings.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!brand && (!name || !url)}>
          {brand ? t('brandsSettings.save') : t('brandsSettings.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

